import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { HttpClientModule } from "@angular/common/http";

import { MatSliderModule } from '@angular/material/slider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AppComponent } from './app.component';
import { NavigationComponent } from './navigation/navigation.component';
import { OnSaleComponent } from './on-sale/on-sale.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { HomeComponent } from './home/home.component';
import { WishlistComponent } from './wishlist/wishlist.component';
import { BlogComponent } from './blog/blog.component';
import { SearchComponent } from './search/search.component';
import { ContactComponent } from './contact/contact.component';
import { ImprintComponent } from './imprint/imprint.component';
import { LoginComponent } from './login/login.component';
import { RegisterComponent } from './register/register.component';
import { MediaSelectorComponent } from './media-selector/media-selector.component';
import { StoreSelectorComponent } from './store-selector/store-selector.component';
import { HeaderComponent } from './header/header.component';
import { NavigationBarComponent } from './navigation-bar/navigation-bar.component';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { BaseSearchComponent } from './classes/BaseSearchComponent';
import { RatingsComponent } from './ratings/ratings.component';
import { GridItemPricesComponent } from './grid-item-prices/grid-item-prices.component';
import { GridItemComponent } from './grid-item/grid-item.component';
import { DateSeparatorComponent } from './date-separator/date-separator.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { SmallFlagsComponent } from './small-flags/small-flags.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { SpecialListsComponent } from './special-lists/special-lists.component';
import { CarouselComponent } from './carousel/carousel.component';
import { BlogPostComponent } from './blog-post/blog-post.component';
import { ScrollTopComponent } from './scroll-top/scroll-top.component';
import { RankComponent } from './rank/rank.component';
import { VerticalMediaSelectorComponent } from './vertical-media-selector/vertical-media-selector.component';
import { DetailViewComponent } from './detail-view/detail-view.component';
import { ExpandableComponent } from './expandable/expandable.component';
import { PriceEvolutionComponent } from './price-evolution/price-evolution.component';
import { DetailPriceComponent } from './detail-price/detail-price.component';
import { TrailerComponent } from './trailer/trailer.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { InputFieldComponent } from './input-field/input-field.component';
import { RegisterSuccessComponent } from './register-success/register-success.component';
import { DrawerComponent } from './drawer/drawer.component';
import { ModalComponent } from './modal/modal.component';
import { FeedbackButtonComponent } from './feedback-button/feedback-button.component';
import { FeedbackFormComponent } from './feedback-form/feedback-form.component';
import { OtherSeasonsComponent } from './other-seasons/other-seasons.component';
import { SpinnerOverlayComponent } from './spinner-overlay/spinner-overlay.component';
import { ChildSinglesComponent } from './child-singles/child-singles.component';
import { ChildMoviesComponent } from './child-movies/child-movies.component';
import { HomeFooterComponent } from './home-footer/home-footer.component';
import { HorizontalScrollGalleryComponent } from './horizontal-scroll-gallery/horizontal-scroll-gallery.component';
import { RegisterChoiceComponent } from './register-choice/register-choice.component';
import { FacebookLoginButtonComponent } from './facebook-login-button/facebook-login-button.component';
import { RegisterUsernameComponent } from './register-username/register-username.component';
import { NotificationSettingsComponent } from './notification-settings/notification-settings.component';
import { ChangeEmailPasswordComponent } from './change-email-password/change-email-password.component';
import { CustomCheckboxComponent } from './custom-checkbox/custom-checkbox.component';
import { FullscreenGalleryComponent } from './fullscreen-gallery/fullscreen-gallery.component';
import { GridItemWishlistComponent } from './grid-item-wishlist/grid-item-wishlist.component';
import { MyErrorHandler } from './classes/MyErrorHandler';
import { CrashReportService } from './crash-report.service';
import { DisclaimerComponent } from './disclaimer/disclaimer.component';
import { WishlistCelebrationComponent } from './wishlist-celebration/wishlist-celebration.component';
import { ErrorComponent } from './error/error.component';
import { CookiesMessageComponent } from './cookies-message/cookies-message.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { MovieRatingsSlidersComponent } from './movie-ratings-sliders/movie-ratings-sliders.component';
import { MultipickDropdownComponent } from './multipick-dropdown/multipick-dropdown.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { ServerDownComponent } from './server-down/server-down.component';
import { AutofocusDirective } from './autofocus.directive';
import { AuthenticationComponent } from './authentication/authentication.component';
import { AlsoIncludedComponent } from './also-included/also-included.component';
import { CountryService } from './country.service';
import { RichTextComponent } from './rich-text/rich-text.component';
import { NewsComponent } from './news/news.component';
import { MyPurchasesComponent } from './my-purchases/my-purchases.component';
import { GridItemOwnlistComponent } from './grid-item-ownlist/grid-item-ownlist.component';
import { SlideContentComponent } from './slide-content/slide-content.component';
import { ImportItunesLibComponent } from './import-itunes-lib/import-itunes-lib.component';
import { ImportVuduLibComponent } from './import-vudu-lib/import-vudu-lib.component';
import { GraphComponent } from './graph/graph.component';
import { PreferencesComponent } from './preferences/preferences.component';
import { SettingsService } from './settings.service';
import { FeedbackPromptComponent } from './feedback-prompt/feedback-prompt.component';
import { FormsModule } from '@angular/forms';
import { RatingComponent } from './rating/rating.component';
import { GiftCardsComponent } from './gift-cards/gift-cards.component';
import { NewCardDealComponent } from './new-card-deal/new-card-deal.component';
import { CardDealItemComponent } from './card-deal-item/card-deal-item.component';
import { CardDealDetailComponent } from './card-deal-detail/card-deal-detail.component';
import { ThemeService } from './theme.service';
import { FeedbackSmileyComponent } from './feedback-smiley/feedback-smiley.component';
import { SeoService } from './seo.service';
import { CategoriesBarComponent } from './categories-bar/categories-bar.component';
import { ToggleSwitchComponent } from './toggle-switch/toggle-switch.component';
import { GlobalService } from './global.service';
import { InfoService } from './info.service';
import { ShareComponent } from './share/share.component';
import { ReviewsComponent } from './reviews/reviews.component';
import { ReviewComponent } from './review/review.component';
import { ImportImdbListComponent } from './import-imdb-list/import-imdb-list.component';
import { UserReviewsComponent } from './user-reviews/user-reviews.component';
import { SliderMultichoiceComponent } from './slider-multichoice/slider-multichoice.component';
import { RangeSliderComponent } from './range-slider/range-slider.component';
import { MultichoiceMenuComponent } from './multichoice-menu/multichoice-menu.component';
import { FilterDropdownComponent } from './filter-dropdown/filter-dropdown.component';
import { ItunesMovieDealsComponent } from './itunes-movie-deals/itunes-movie-deals.component';
import { ItunesMovies4KComponent } from './itunes-movies-4k/itunes-movies-4k.component';
import { SingleCarouselComponent } from './single-carousel/single-carousel.component';
import { SingleReviewComponent } from './single-review/single-review.component';
import { PrimeVideoDealsComponent } from './prime-video-deals/prime-video-deals.component';
import { VuduMovieDealsComponent } from './vudu-movie-deals/vudu-movie-deals.component';
import CustomUrlSerialize from './custom-url-serializer';
import { UrlSerializer } from '@angular/router';
import { RedirectGuard } from './RedirectGuard';
import { CommaSeparatedComponent } from './comma-separated/comma-separated.component';
import { NgxJsonLdModule } from '@ngx-lite/json-ld';
import { RibbonComponent } from './ribbon/ribbon.component';
import { SignInAppleComponent } from './sign-in-apple/sign-in-apple.component';
import { AppleRedirectComponent } from './apple-redirect/apple-redirect.component';
import { ChooseUsernameComponent } from './choose-username/choose-username.component';
import { AccountSettingsComponent } from './account-settings/account-settings.component';
import { ChangeEmailComponent } from './change-email/change-email.component';
import { ChangeUsernameComponent } from './change-username/change-username.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { SliderYearComponent } from './slider-year/slider-year.component';
import { SliderRuntimeComponent } from './slider-runtime/slider-runtime.component';
import { ReferralsComponent } from './referrals/referrals.component';
import { RedeemComponent } from './redeem/redeem.component';
import { NavSearchBarComponent } from './nav-search-bar/nav-search-bar.component';
import { SearchSuggestionsComponent } from './search-suggestions/search-suggestions.component';
import { HorizontalScrollComponent } from './horizontal-scroll/horizontal-scroll.component';
import { AdBannerComponent } from './ad-banner/ad-banner.component';
import { ActorsComponent } from './actors/actors.component';
import { StudiosComponent } from './studios/studios.component';
import { BlocklistComponent } from './blocklist/blocklist.component';
import { GridItemBlocklistComponent } from './grid-item-blocklist/grid-item-blocklist.component';
import { MyListsComponent } from './my-lists/my-lists.component';
import { CustomListDetailComponent } from './custom-list-detail/custom-list-detail.component';
import { CustomListService } from './custom-list.service';
import { GamesBannerComponent } from './games-banner/games-banner.component';
import { CookieSettingsComponent } from './cookie-settings/cookie-settings.component';
import { FaqComponent } from './faq/faq.component';
import { ImportLetterboxdListComponent } from './import-letterboxd-list/import-letterboxd-list.component';
import { PriceInformationComponent } from './price-information/price-information.component';
import { BundleCarouselComponent } from './bundle-carousel/bundle-carousel.component';
import { PriceAlertModalComponent } from './price-alert-modal/price-alert-modal.component';
import { ToastComponent } from './toast/toast.component';
import { ToastService } from './toast/toast.service';
import { CountryPollComponent } from './country-poll/country-poll.component';
import { AiLandingComponent } from './ai-landing/ai-landing.component';

const customUrlSerializer = new CustomUrlSerialize();

const CustomUrlSerializerProvider = {
  provide: UrlSerializer,
  useValue: customUrlSerializer
};

@NgModule({
  declarations: [
    AppComponent,
    NavigationComponent,
    OnSaleComponent,
    PageNotFoundComponent,
    HomeComponent,
    WishlistComponent,
    BlogComponent,
    SearchComponent,
    ContactComponent,
    ImprintComponent,
    LoginComponent,
    RegisterComponent,
    MediaSelectorComponent,
    StoreSelectorComponent,
    HeaderComponent,
    NavigationBarComponent,
    SearchBarComponent,
    BaseSearchComponent,
    RatingsComponent,
    GridItemPricesComponent,
    GridItemComponent,
    DateSeparatorComponent,
    SpinnerComponent,
    SmallFlagsComponent,
    DropdownComponent,
    SpecialListsComponent,
    CarouselComponent,
    BlogPostComponent,
    ScrollTopComponent,
    RankComponent,
    VerticalMediaSelectorComponent,
    DetailViewComponent,
    ExpandableComponent,
    PriceEvolutionComponent,
    DetailPriceComponent,
    TrailerComponent,
    ResetPasswordComponent,
    InputFieldComponent,
    RegisterSuccessComponent,
    DrawerComponent,
    ModalComponent,
    FeedbackButtonComponent,
    FeedbackFormComponent,
    OtherSeasonsComponent,
    SpinnerOverlayComponent,
    ChildSinglesComponent,
    ChildMoviesComponent,
    HomeFooterComponent,
    HorizontalScrollGalleryComponent,
    RegisterChoiceComponent,
    FacebookLoginButtonComponent,
    RegisterUsernameComponent,
    NotificationSettingsComponent,
    ChangeEmailPasswordComponent,
    CustomCheckboxComponent,
    FullscreenGalleryComponent,
    GridItemWishlistComponent,
    DisclaimerComponent,
    WishlistCelebrationComponent,
    ErrorComponent,
    CookiesMessageComponent,
    PrivacyPolicyComponent,
    MovieRatingsSlidersComponent,
    MultipickDropdownComponent,
    CheckboxComponent,
    ServerDownComponent,
    AutofocusDirective,
    AuthenticationComponent,
    AlsoIncludedComponent,
    RichTextComponent,
    NewsComponent,
    MyPurchasesComponent,
    GridItemOwnlistComponent,
    SlideContentComponent,
    ImportItunesLibComponent,
    ImportVuduLibComponent,
    GraphComponent,
    PreferencesComponent,
    FeedbackPromptComponent,
    RatingComponent,
    GiftCardsComponent,
    NewCardDealComponent,
    CardDealItemComponent,
    CardDealDetailComponent,
    FeedbackSmileyComponent,
    CategoriesBarComponent,
    ToggleSwitchComponent,
    ShareComponent,
    ReviewsComponent,
    ReviewComponent,
    ImportImdbListComponent,
    ImportLetterboxdListComponent,
    UserReviewsComponent,
    SliderMultichoiceComponent,
    RangeSliderComponent,
    MultichoiceMenuComponent,
    FilterDropdownComponent,
    ItunesMovieDealsComponent,
    ItunesMovies4KComponent,
    SingleCarouselComponent,
    SingleReviewComponent,
    PrimeVideoDealsComponent,
    VuduMovieDealsComponent,
    CommaSeparatedComponent,
    RibbonComponent,
    SignInAppleComponent,
    AppleRedirectComponent,
    ChooseUsernameComponent,
    AccountSettingsComponent,
    ChangeEmailComponent,
    ChangeUsernameComponent,
    ChangePasswordComponent,
    SliderYearComponent,
    SliderRuntimeComponent,
    ReferralsComponent,
    RedeemComponent,
    NavSearchBarComponent,
    SearchSuggestionsComponent,
    HorizontalScrollComponent,
    AdBannerComponent,
    ActorsComponent,
    StudiosComponent,
    BlocklistComponent,
    GridItemBlocklistComponent,
    MyListsComponent,
    CustomListDetailComponent,
    GamesBannerComponent,
    CookieSettingsComponent,
    FaqComponent,
    PriceInformationComponent,
    BundleCarouselComponent,
    PriceAlertModalComponent,
    ToastComponent,
    CountryPollComponent,
    AiLandingComponent,
  ],
  imports: [
    FormsModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    MatSliderModule,
    MatProgressSpinnerModule,
    NgxJsonLdModule
  ],
  providers: [
    CrashReportService,
    CountryService,
    { provide: ErrorHandler, useClass: MyErrorHandler },
    SettingsService,
    ThemeService,
    SeoService,
    GlobalService,
    InfoService,
    CustomListService,
    CustomUrlSerializerProvider,
    RedirectGuard,
    ToastService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
